import { useState } from 'react';
import { ExternalLink, MousePointerClick } from 'lucide-react';
import { ErrorState, Skeleton } from '@/components/States';
import { useCampaignLinks } from '@/hooks/use-send';
import { cn } from '@/lib/utils';

/**
 * The click map — every link in the email, in the order a reader met them, with its share of clicks.
 *
 * ── WHAT THIS IS NOT ──────────────────────────────────────────────────────────────
 *
 * Not an overlay on the rendered email. The source spec defers that explicitly: "expensive to build;
 * the per-link table with `links.position` delivers most of the value". This is that table, and the
 * overlay stays deferred rather than being quietly built because it sounds better.
 *
 * ── WHY DOCUMENT ORDER IS THE DEFAULT ─────────────────────────────────────────────
 *
 * The report already ranks links by clicks. Ranking answers "what was popular"; ORDER answers "did
 * the hero outperform the footer", which is the question that changes what you write next. A list
 * sorted by clicks cannot answer it, because the position information has been sorted away.
 *
 * ── ZERO IS A RESULT ──────────────────────────────────────────────────────────────
 *
 * Links nobody clicked are shown, with an explicit "0". They are the most useful rows in the table:
 * a call to action at position 1 with no clicks is a finding, and the report's click-ranked top 50
 * drops it off the end.
 *
 * ── THE BAR IS RELATIVE TO THE BEST LINK, NOT TO THE TOTAL ────────────────────────
 *
 * Share-of-total makes every bar tiny as soon as an email has twenty links, which hides the shape.
 * Scaling to the most-clicked link means the comparison a reader wants — this one against the best
 * one — is the comparison the bars actually show. The figure beside it is the real number, so the
 * scaling cannot mislead anybody about magnitude.
 */
export function ClickMap({ campaignId }: { campaignId: string }) {
  const links = useCampaignLinks(campaignId);
  const [isExpanded, setExpanded] = useState(false);

  if (links.isPending) return <Skeleton className="h-48" />;

  if (links.isError) {
    return <ErrorState error={links.error} onRetry={() => void links.refetch()} />;
  }

  const all = links.data.items;

  if (all.length === 0) {
    return (
      <section className="overflow-hidden rounded-lg border border-border bg-surface">
        <Header count={0} />
        <p className="px-4 py-6 text-sm text-muted">
          No links were found in this campaign. Nothing to map — and if that is a surprise, the email
          may be using an unsubscribe link the linter did not recognise as one.
        </p>
      </section>
    );
  }

  // Scale to the busiest link. Guarded at 1 so an all-zero table renders flat bars rather than
  // dividing by zero and painting every row full width.
  const busiest = Math.max(1, ...all.map((link) => link.humanTotalClicks));

  const COLLAPSED = 12;
  const shown = isExpanded ? all : all.slice(0, COLLAPSED);
  const clicked = all.filter((link) => link.humanTotalClicks > 0).length;

  return (
    <section className="overflow-hidden rounded-lg border border-border bg-surface">
      <Header count={all.length} clicked={clicked} />

      <ol className="divide-y divide-border">
        {shown.map((link) => {
          const share = Math.round((link.humanTotalClicks / busiest) * 100);

          return (
            <li key={`${link.position}-${link.url}`} className="px-4 py-3">
              <div className="flex items-baseline gap-3">
                {/* Document position, monospaced so the column stays aligned as it passes 9. */}
                <span
                  className="w-6 shrink-0 font-mono text-xs tabular-nums text-muted"
                  aria-hidden="true"
                >
                  {link.position + 1}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 text-sm text-foreground">
                    <ExternalLink
                      className="size-3.5 shrink-0 text-muted"
                      aria-hidden="true"
                    />
                    <span className="truncate">{link.linkText ?? link.url}</span>
                  </span>

                  {link.linkText !== null && (
                    <span className="mt-0.5 block truncate pl-5 text-xs text-muted">{link.url}</span>
                  )}
                </span>

                <span className="shrink-0 text-right">
                  <span
                    className={cn(
                      'block text-sm font-medium tabular-nums',
                      link.humanTotalClicks === 0 ? 'text-muted' : 'text-heading',
                    )}
                  >
                    {link.humanTotalClicks.toLocaleString()}
                  </span>

                  {link.totalClicks !== link.humanTotalClicks && (
                    <span className="block text-xs tabular-nums text-muted">
                      {link.totalClicks.toLocaleString()} with bots
                    </span>
                  )}
                </span>
              </div>

              {/* The bar carries no information the figure above does not, so it is hidden from
                  assistive technology rather than announced as a second, vaguer version of it. */}
              <div className="mt-2 ml-9 h-1.5 overflow-hidden rounded-full bg-surface-2" aria-hidden="true">
                <div
                  className={cn('h-full rounded-full', share === 0 ? '' : 'bg-primary')}
                  style={{ width: `${share}%` }}
                />
              </div>
            </li>
          );
        })}
      </ol>

      {all.length > COLLAPSED && (
        <div className="border-t border-border px-4 py-2.5">
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            aria-expanded={isExpanded}
            className="text-sm text-link underline underline-offset-2"
          >
            {isExpanded ? 'Show fewer' : `Show all ${all.length} links`}
          </button>
        </div>
      )}
    </section>
  );
}

function Header({ count, clicked }: { count: number; clicked?: number }) {
  return (
    <div className="border-b border-border px-4 py-3">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-heading">
        <MousePointerClick className="size-4 text-muted" aria-hidden="true" />
        Links, in the order they appear
      </h2>

      {count > 0 && clicked !== undefined && (
        <p className="mt-0.5 text-xs text-muted">
          {clicked} of {count} {count === 1 ? 'link' : 'links'} {clicked === 1 ? 'was' : 'were'}{' '}
          clicked by a person. Bars are relative to the busiest link, not to the total.
        </p>
      )}
    </div>
  );
}
